/**
 * Hilo del chat con IA del admin: burbujas del usuario con sus acciones (Repetir en una
 * conversación nueva, Editar, Copiar) y respuestas del asistente con avatar y acciones. Cómo se
 * pinta cada respuesta lo decide el agente (`renderRespuesta`: markdown de la guía, tarjetas de
 * acción del analista, párrafos con citas del CEO); su pie también (`renderPie`), con `Copiar`
 * por omisión.
 *
 * Exporta además las piezas sueltas (`AccionMensaje`, `BotonCopiar`, `ChatIaAviso`,
 * `EsqueletoHilo`) para que los pies propios de cada agente se vean igual.
 */
import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { Check, Copy, Pencil, Repeat2, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconTip } from "@/components/ui/icon-tip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface MensajeChatIa {
  id: string;
  rol: "usuario" | "asistente";
  texto: string;
  /** Se pinta tenue (p. ej. quitado del contexto en el CEO). */
  atenuado?: boolean;
}

export interface ChatIaMensajesProps<T extends MensajeChatIa> {
  mensajes: T[];
  renderRespuesta: (m: T) => ReactNode;
  /** Pie de una respuesta; por omisión, solo Copiar. */
  renderPie?: (m: T) => ReactNode;
  /** Texto que copia el botón de una respuesta (por omisión, el texto tal cual). */
  textoCopiar?: (texto: string) => string;
  /** Repetir una pregunta (siempre en una conversación nueva). Sin él no hay botón. */
  onRepetir?: (texto: string) => void;
  /** Llevar la pregunta al composer. Sin él (p. ej. solo lectura) no hay botón. */
  onEditar?: (texto: string) => void;
  /** Deshabilita Repetir mientras se responde o hay límite. */
  deshabilitado?: boolean;
  /** Muestra el indicador de "escribiendo" al final del hilo. */
  escribiendo?: boolean;
  /** Texto junto al indicador (p. ej. "Analizando"). Sin él, tres puntos. */
  textoEscribiendo?: string | null;
  icono?: ComponentType<{ className?: string }>;
}

export function ChatIaMensajes<T extends MensajeChatIa>({
  mensajes,
  renderRespuesta,
  renderPie,
  textoCopiar,
  onRepetir,
  onEditar,
  deshabilitado,
  escribiendo,
  textoEscribiendo,
  icono = Sparkles,
}: ChatIaMensajesProps<T>) {
  return (
    <div className="space-y-5">
      {mensajes.map((m) =>
        m.rol === "usuario" ? (
          <MensajeUsuario
            key={m.id}
            texto={m.texto}
            atenuado={m.atenuado}
            deshabilitado={!!deshabilitado}
            onRepetir={onRepetir ? () => onRepetir(m.texto) : undefined}
            onEditar={onEditar ? () => onEditar(m.texto) : undefined}
          />
        ) : (
          <div key={m.id} className={cn("group/msg flex gap-3", m.atenuado && "opacity-50")}>
            <AvatarAsistente icono={icono} />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
              <div className="w-full min-w-0 break-words text-[13.5px] leading-relaxed text-foreground">
                {renderRespuesta(m)}
              </div>
              {renderPie ? (
                renderPie(m)
              ) : (
                <AccionesMensaje className="-ml-2">
                  <BotonCopiar texto={textoCopiar ? textoCopiar(m.texto) : m.texto} />
                </AccionesMensaje>
              )}
            </div>
          </div>
        ),
      )}

      {escribiendo && (
        <div className="flex gap-3" role="status" aria-label={textoEscribiendo || "Escribiendo"}>
          <AvatarAsistente icono={icono} />
          <span className="inline-flex items-center gap-2 rounded-2xl rounded-tl-md bg-muted/60 px-4 py-3 text-[12.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </span>
            {textoEscribiendo && <span className="italic">{textoEscribiendo}</span>}
          </span>
        </div>
      )}
    </div>
  );
}

function MensajeUsuario({
  texto,
  atenuado,
  deshabilitado,
  onRepetir,
  onEditar,
}: {
  texto: string;
  atenuado?: boolean;
  deshabilitado: boolean;
  onRepetir?: () => void;
  onEditar?: () => void;
}) {
  return (
    <div className={cn("group/msg flex flex-col items-end gap-1", atenuado && "opacity-50")}>
      <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-[13.5px] text-primary-foreground shadow-sm">
        {texto}
      </p>
      <AccionesMensaje>
        {onRepetir && (
          <AccionMensaje etiqueta="Repetir en una conversación nueva" disabled={deshabilitado} onClick={onRepetir}>
            <Repeat2 />
            Repetir
          </AccionMensaje>
        )}
        {onEditar && (
          <AccionMensaje etiqueta="Llevar al cuadro de texto para cambiarla" onClick={onEditar}>
            <Pencil />
            Editar
          </AccionMensaje>
        )}
        <BotonCopiar texto={texto} />
      </AccionesMensaje>
    </div>
  );
}

/** Fila de acciones de un mensaje: siempre visible en táctil, al pasar el mouse desde `md`. */
export function AccionesMensaje({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-0.5 opacity-100 transition-opacity md:opacity-0 md:focus-within:opacity-100 md:group-hover/msg:opacity-100",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function AccionMensaje({
  etiqueta,
  disabled,
  activo,
  onClick,
  children,
}: {
  etiqueta: string;
  disabled?: boolean;
  /** Marca la acción como elegida (p. ej. el pulgar votado). */
  activo?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconTip label={etiqueta}>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={etiqueta}
        aria-pressed={activo}
        className={cn(
          "inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-3.5",
          activo && "bg-muted text-foreground",
        )}
      >
        {children}
      </button>
    </IconTip>
  );
}

export function BotonCopiar({ texto, etiqueta = "Copiar" }: { texto: string; etiqueta?: string }) {
  const [copiado, setCopiado] = useState(false);
  useEffect(() => {
    if (!copiado) return;
    const t = window.setTimeout(() => setCopiado(false), 1600);
    return () => window.clearTimeout(t);
  }, [copiado]);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
    } catch {
      // Portapapeles bloqueado: no hay nada que hacer, el texto sigue seleccionable.
    }
  };
  return (
    <AccionMensaje etiqueta="Copiar texto" onClick={() => void copiar()}>
      {copiado ? <Check className="text-emerald-600" /> : <Copy />}
      {copiado ? "Copiado" : etiqueta}
    </AccionMensaje>
  );
}

export function AvatarAsistente({ icono: Icono = Sparkles }: { icono?: ComponentType<{ className?: string }> }) {
  return (
    <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Icono className="size-3.5" />
    </span>
  );
}

export function EsqueletoHilo() {
  return (
    <div className="space-y-5" aria-label="Abriendo conversación" role="status">
      <Skeleton className="ml-auto h-9 w-1/2 rounded-2xl" />
      <div className="flex gap-3">
        <Skeleton className="size-7 shrink-0 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
}

/** Aviso bajo el hilo: error (rojo) o límite/espera (ámbar), con "Reintentar" opcional. */
export function ChatIaAviso({
  tono = "error",
  mensaje,
  detalle,
  onReintentar,
  reintentando,
}: {
  tono?: "error" | "aviso";
  mensaje: string;
  detalle?: ReactNode;
  onReintentar?: () => void;
  reintentando?: boolean;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "mt-4 space-y-2 rounded-lg border px-3 py-2 text-[12px]",
        tono === "aviso"
          ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300"
          : "border-destructive/30 bg-destructive/5 text-destructive",
      )}
    >
      <p className="break-words">{mensaje}</p>
      {detalle && <p className="font-medium">{detalle}</p>}
      {onReintentar && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={reintentando}
          onClick={onReintentar}
          className="h-7 px-2.5 text-[12px]"
        >
          <RotateCcw />
          Reintentar
        </Button>
      )}
    </div>
  );
}
